import { ActivityType } from '@/components/Activities'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    Product,
    ServiceContractLink,
    ServiceTimes,
} from '@/modules/Contract/types'
import { Attachment } from '@/modules/Upload/types'
import { ResponseArray, ResponseObject } from '@/types/shared'
import datacentral from '@/utils/axios/datacentral'
import { uniqBy } from '@/utils/function'
import { TFunction } from 'i18next'
import queryString from 'query-string'
import { DataGridPayload } from 'vnetwork-ui/dist/components/GokuDataGrid'
import { AlertThreshold } from '../components/Detail/TabComponents/Alerts/Threshold'
import { ServiceDomainMetadata } from '../components/Detail/Templates/Domain/DomainVNNIC/types'
import { ServiceInformationType } from '../components/Detail/Templates/shared/ServiceNotifications'
import { SERVICE_STATUS } from '../constants'
import {
    AnalyticParams,
    GetServiceOptionsRow,
    Provider,
    Service,
    ServiceAlert,
    ServiceCancelProps,
    ServiceConfigoption,
    ServiceCustomerMailer,
    ServiceDate,
    ServiceInformation,
    ServiceInvoice,
    ServiceMailerType,
    ServiceNotification,
    ServiceStatus,
    ServiceUpdate,
    ServiceVNISProvider,
    ServicePrice,
    ServiceVatRate,
    CreateServicePricePayload,
    UpdateServicePricePayload,
    PriceChangeRequest,
} from '../types'

export const getServiceByCustomerID = async ({
    t,
    payload,
    customerID,
}: {
    t: TFunction
    payload: DataGridPayload & {
        // Narrows the list to services no live contract and no other live annex already owns —
        // the same rule the annex write path enforces, so the picker stops offering options the
        // server would reject. Omitted means "unchanged behaviour".
        eligible_for_subcontract?: boolean
        // The annex being edited, whose own services must stay selectable.
        current_subcontract_id?: number
    }
    customerID: number
}) => {
    const response = await datacentral.get<ResponseArray<GetServiceOptionsRow>>(
        `/services/customers/${customerID}?${queryString.stringify(payload)}`
    )

    if (!response?.data?.results) return []

    return response?.data?.results?.map((service) => ({
        label: [
            service.service_id,
            service?.service_name,
            t(`services:service-status.${service?.service_status}`),
        ]
            .filter(Boolean)
            .join(' - '),
        value: service.service_id,
    }))
}

export const getServices = async (
    payload: DataGridPayload & {
        columns?: string
        export?: string
        filename?: string
    }
) => {
    const response = await datacentral.get<ResponseArray<Service>>(
        `/services?${queryString.stringify(payload)}`
    )
    return response?.data
}

export const getWaitingConfirmServices = async (payload: DataGridPayload) => {
    const response = await datacentral.get<ResponseArray<Service>>(
        `/services/waiting-confirmations?${queryString.stringify(payload)}`
    )
    return response?.data
}

export const getPOCServices = async (payload: DataGridPayload) => {
    const response = await datacentral.get<ResponseArray<Service>>(
        `/services/poc?${queryString.stringify(payload)}`
    )
    return response?.data
}

export const confirmService = async (serviceID: number) => {
    const response = await datacentral.put<ResponseObject<Service>>(
        `/services/${serviceID}/confirmed`
    )
    return response?.data
}

export const rejectedService = async ({
    serviceID,
    reason,
}: {
    serviceID: number
    reason: string
}) => {
    const response = await datacentral.put<ResponseObject<Service>>(
        `/services/${serviceID}/rejected`,
        {
            message: reason,
        }
    )
    return response?.data
}

export const closeServicePOC = async ({
    serviceID,
    reason,
}: {
    serviceID: number
    reason: string
}) => {
    const response = await datacentral.put<ResponseObject<Service>>(
        `/services/${serviceID}/poc/closed`,
        {
            message: reason,
        }
    )
    return response?.data
}

export const getService = async (serviceID: number) => {
    const response = await datacentral.get<ResponseObject<Service>>(
        `/services/${serviceID}`
    )
    return response?.data?.result
}

export const getServiceUpdates = async ({
    payload,
    serviceID,
}: {
    serviceID: number
    payload: Pick<DataGridPayload, 'page' | 'page_size'>
}): Promise<ResponseArray<any>> => {
    const response = await datacentral.get<ResponseArray<ActivityType>>(
        `/services/${serviceID}/updates?${queryString.stringify(payload)}`
    )
    return response?.data
}
export const getServiceContract = async (serviceID: number) => {
    const response = await datacentral.get<ResponseArray<ServiceContractLink>>(
        `/services/${serviceID}/contract`
    )
    return response?.data?.results || []
}

export const getServiceInvoices = async ({
    serviceID,
    ...payload
}: DataGridPayload & { serviceID: number }) => {
    const response = await datacentral.get<ResponseArray<ServiceInvoice>>(
        `/services/${serviceID}/invoices?${queryString.stringify(payload)}`
    )
    return response?.data
}

export const createServiceNotification = async ({
    serviceID,
    data,
    attachments,
}: {
    serviceID: number
    data: ServiceInformationType
    attachments: Attachment[]
}) => {
    const response = await datacentral.post(
        `/services/${serviceID}/technical`,
        {
            service_information: data,
            attachments: attachments,
        }
    )
    return response?.data
}

export const getServiceNotifications = async ({
    serviceID,
}: {
    serviceID: number
}) => {
    const response = await datacentral.get<ResponseArray<ServiceNotification>>(
        `/services/${serviceID}/technical`
    )
    return response?.data?.results
}

export const updateServiceNotification = async ({
    serviceID,
    notificationID,
    data,
    attachments,
}: {
    serviceID: number
    notificationID: number
    data: ServiceInformationType
    attachments: Attachment[]
}) => {
    const response = await datacentral.put(
        `/services/${serviceID}/technical/${notificationID}`,
        {
            service_information: data,
            attachments: attachments,
        }
    )
    return response?.data
}

export const deleteServiceNotification = async ({
    serviceID,
    notificationID,
}: {
    serviceID: number
    notificationID: number
}) => {
    const response = await datacentral.delete(
        `/services/${serviceID}/technical/${notificationID}`
    )
    return response?.data
}

export const registerService = async (serviceID: number) => {
    const response = await datacentral.post(`/services/${serviceID}/register`)
    return response?.data
}

export const renewService = async (serviceID: number) => {
    const response = await datacentral.post(`/services/${serviceID}/renew`)
    return response?.data
}

export const getServiceConfigoptions = async ({
    serviceID,
}: {
    serviceID: number
}) => {
    const response = await datacentral.get<ResponseArray<ServiceConfigoption>>(
        `/services/${serviceID}/configoptions`
    )
    return response?.data?.results
}

export const getServiceConfigoptionPrices = async ({
    serviceID,
}: {
    serviceID: number
}) => {
    const response = await datacentral.get<ResponseArray<ServiceConfigoption>>(
        `/services/${serviceID}/configoptions/prices`
    )
    return response?.data?.results
}

export const updateServiceConfigoptions = async ({
    serviceID,
    service_configoptions,
}: {
    serviceID: number
    service_configoptions: Partial<ServiceConfigoption>[]
}) => {
    const response = await datacentral.put(
        `/services/${serviceID}/configoptions`,
        {
            service_configoptions,
        }
    )
    return response?.data
}

export const updateServiceConfigoptionMetadata = async ({
    serviceID,
    service_configoption_id,
    metadata,
}: {
    serviceID: number
    service_configoption_id: number
    metadata: any
}) => {
    const response = await datacentral.put<ResponseObject<ServiceConfigoption>>(
        `/services/${serviceID}/configoptions/metadata`,
        {
            service_configoption_id,
            metadata,
        }
    )
    return response?.data?.result
}

export const updateServiceConfigoptionPrices = async ({
    serviceID,
    service_configoptions,
}: {
    serviceID: number
    service_configoptions: Array<{
        price_exceed_id: number
        method: string
        price_ranges: any[]
    }>
}) => {
    const response = await datacentral.put(
        `/services/${serviceID}/configoptions/prices`,
        {
            service_configoptions,
        }
    )
    return response?.data
}

export const getServiceDate = async ({ serviceID }: { serviceID: number }) => {
    const response = await datacentral.get<ResponseObject<ServiceDate>>(
        `/services/${serviceID}/dates`
    )
    return response?.data?.result
}

export const getServiceNotificationMailers = async ({
    serviceID,
}: {
    serviceID: number
}) => {
    const response = await datacentral.get<
        ResponseArray<ServiceCustomerMailer>
    >(`/services/${serviceID}/technical/mailers`)
    return response?.data?.results
}

export const updateServiceEntityName = async ({
    serviceID,
    name,
}: {
    serviceID: number
    name: string
}) => {
    const response = await datacentral.put(
        `/services/${serviceID}/technical/entity_name`,
        {
            name: name,
        }
    )
    return response?.data
}

export const cancelService = async ({
    serviceID,
    data,
}: {
    serviceID: number
    data: ServiceCancelProps
}) => {
    const response = await datacentral.put(`/services/${serviceID}/cancel`, {
        ...data,
    })
    return response?.data
}

export const createServiceNotes = async ({
    serviceID,
    data,
}: {
    serviceID: number
    data: Partial<ServiceUpdate>
}) => {
    const response = await datacentral.post(
        `/services/${serviceID}/notes`,
        data
    )
    return response?.data
}

export const updateService = async ({
    serviceID,
    data,
}: {
    serviceID: number
    data: Partial<Service>
}) => {
    const response = await datacentral.put(`/services/${serviceID}`, data)
    return response?.data
}

export const createManualInvoice = async ({
    serviceID,
    reason = '',
    action = 'renew',
}: {
    serviceID: number
    reason: string
    action: string
}) => {
    const response = await datacentral.post(
        `/services/${serviceID}/invoices/${action}`,
        {
            reason,
        }
    )
    return response?.data
}

export const createStatementInvoice = async ({
    serviceID,
    reason = '',
}: {
    serviceID: number
    reason: string
}) => {
    const response = await datacentral.post(
        `/services/${serviceID}/invoices/statement`,
        {
            reason,
        }
    )
    return response?.data
}

export interface CreateFeeInvoicePayload {
    serviceID: number
    start_date: string
    end_date: string
    fee_amount: number
    fee_name?: string
    description?: string
}

export const createFeeInvoice = async ({
    serviceID,
    ...data
}: CreateFeeInvoicePayload) => {
    const response = await datacentral.post(
        `/services/${serviceID}/invoices/fee`,
        data
    )
    return response?.data
}

export const getProviders = async () => {
    const response =
        await datacentral.get<ResponseArray<Provider>>(`/services/providers`)
    if (!response?.data) return []
    return response?.data?.results
}

export const getServiceInformation = async (serviceID: number) => {
    const response = await datacentral.get<ResponseArray<ServiceInformation>>(
        `/services/${serviceID}/service_informations`
    )
    return response?.data
}

export const deliveryNotificationMailer = async ({
    serviceID,
    notificationID,
    mailerID,
    mailerParams,
}: {
    serviceID: number
    notificationID: number
    mailerID: number
    mailerParams: {
        mailer_receivers?: string
        mailer_cc?: string
        mailer_bcc?: string
        ticket_id?: number
    }
}) => {
    const response = await datacentral.post(
        `/services/${serviceID}/technical/${notificationID}?mailer_id=${mailerID}`,
        mailerParams
    )
    return response?.data?.result
}

export const getServiceNotificationMailerTrackings = async ({
    serviceID,
}: {
    serviceID: number
}) => {
    const response = await datacentral.get<ResponseArray<ServiceMailerType>>(
        `/services/${serviceID}/technical/notifications`
    )
    return response?.data
}

export const getServiceAnalyticUsages = async ({
    serviceID,
    params,
}: {
    serviceID: number
    params: AnalyticParams
}) => {
    const response = await datacentral.get(
        `/services/${serviceID}/analytics?${queryString.stringify(params)}`
    )
    return response?.data?.data
}

// Streams the multi-sheet CDN Business Usage XLSX workbook (proxied from servicemgmt
// v3). Returns the raw blob response so the caller can trigger a download.
export const exportServiceBusinessUsage = async ({
    serviceID,
    gte,
    lte,
    metrics,
    views,
}: {
    serviceID: number
    gte: string
    lte: string
    metrics: string[]
    views: string[]
}) => {
    return datacentral.get(
        `/services/${serviceID}/analytics/business/export?${queryString.stringify(
            { gte, lte, metrics: metrics.join(','), views: views.join(',') }
        )}`,
        { responseType: 'blob' }
    )
}

export const getServicesStatistical = async (
    payload: Omit<DataGridPayload, 'page' | 'page_size'>
): Promise<
    ResponseArray<{
        is_poc: boolean
        service_status: (typeof ServiceStatus)[keyof typeof ServiceStatus]
        count: number
    }>
> => {
    const response = await datacentral.get(
        `/services/statistical?${queryString.stringify(payload)}`
    )
    return response?.data
}

export const updateServicePOCResponse = async ({
    serviceID,
    data,
}: {
    serviceID: number
    data: {
        service_stopped_date?: string | null
        service_update_description: string
    }
}) => {
    const response = await datacentral.put(
        `/services/${serviceID}/response_of_poc`,
        data
    )
    return response?.data
}

export const retentionServicePOC = async ({
    serviceID,
    data,
}: {
    serviceID: number
    data: {
        service_stopped_date?: string | null
        service_update_description: string
    }
}) => {
    const response = await datacentral.put(
        `/services/${serviceID}/retention_of_poc`,
        data
    )
    return response?.data
}

export const updateServiceInformations = async ({
    serviceID,
    data,
    message,
}: {
    serviceID: number
    data: Partial<ServiceInformation>[]
    message: string
}) => {
    const response = await datacentral.put(
        `/services/${serviceID}/service_informations`,
        {
            service_informations: data,
            service_id: serviceID,
            message,
        }
    )
    return response?.data
}

export const updateServiceTime = async ({
    service_id,
    data,
}: {
    service_id: number
    data: Partial<ServiceTimes>
}) => {
    const response = await datacentral.put(
        `/services/${service_id}/service_time`,
        data
    )
    return response?.data
}

export const getServiceMetadata = async (serviceID: number) => {
    const response = await datacentral.get<
        ResponseObject<ServiceDomainMetadata>
    >(`/services/${serviceID}/metadata`)
    return response?.data?.result
}

export const createServiceAddons = async (data: {
    addons: Partial<
        Product & {
            service_parent_id: number
            service_name: string
        }
    >
}) => {
    const response = await datacentral.post(
        `/services/${data.addons?.service_parent_id}/addons`,
        data
    )
    return response?.data
}

export const getServiceAddons = async (serviceID: number) => {
    const response = await datacentral.get<ResponseArray<any>>(
        `/services/${serviceID}/addons`
    )
    return response?.data
}

export const getProviderAddons = async (serviceID: number) => {
    const response = await datacentral.get(`/services/${serviceID}/addons`)
    return response?.data?.data
}

export const getQuotaViewers = async (serviceID: number) => {
    const response = await datacentral.get(
        `/services/${serviceID}/quotas/viewers`
    )
    return response?.data?.data
}

export const getServiceQuotas = async (serviceID: number) => {
    const response = await datacentral.get(`/services/${serviceID}/quotas`)
    return response?.data?.data
}

export const getKubernetesNodepools = async ({
    serviceID,
    addonUUID,
    clusterID,
}: {
    serviceID: number
    addonUUID: string
    clusterID: string
}) => {
    const response = await datacentral.get(
        `/services/${serviceID}/kubernetes/nodepools`,
        { params: { addon_uuid: addonUUID, cluster_id: clusterID } }
    )
    return response?.data?.data
}

export const enableServiceAddon = async ({
    serviceID,
    addonType,
}: {
    serviceID: number
    addonType: string
}) => {
    const response = await datacentral.post(
        `/services/${serviceID}/addons/enable`,
        { addon_type: addonType }
    )
    return response?.data
}
export const getServicesByCustomerID = async (payload: {
    customer_id: number
}) => {
    const response = await datacentral.get<ResponseArray<Service>>(
        `/customers/${payload.customer_id}/services?${queryString.stringify(
            payload
        )}&page=1&page_size=100`
    )
    return response?.data?.results?.map((item) => ({
        label: `${item?.service_id} - ${item?.service_name}`,
        value: item?.service_id,
    }))
}

export const getDeviceLiquidationServices = async (
    payload: DataGridPayload
) => {
    const response = await datacentral.get<ResponseArray<Service>>(
        `/services/device-liquidation?${queryString.stringify(payload)}`
    )
    return response?.data
}

// Statement (reconciliation / đối soát) service list for the Service management
// tab — served by the DataCentral Service module (APP_API_URL, `/services/statement`).
// `date` (YYYY-MM-DD) selects the billing cycle; defaults to today on the server.
export const getStatementServices = async (
    payload: DataGridPayload & { date?: string }
) => {
    const response = await datacentral.get<ResponseArray<Service>>(
        `/services/statement?${queryString.stringify(payload)}`
    )
    return response?.data
}

// Runs statement (đối soát) for a service — creates the statement invoice and
// captures a rollback snapshot. POST /services/:id/invoices/statement.
export const runStatementService = async (serviceID: number) => {
    const response = await datacentral.post<ResponseObject<unknown>>(
        `/services/${serviceID}/invoices/statement`
    )
    return response?.data
}

// Rolls back the latest statement operation for a service and reverts its billing
// period. POST /services/:id/invoices/statement/rollback. May return 4xx with a
// kebab-case error key (surfaced to the user by the caller).
export const rollbackStatementService = async (serviceID: number) => {
    const response = await datacentral.post<
        ResponseObject<{
            service_id: number
            rolled_back_invoice_ids: number[]
        }>
    >(`/services/${serviceID}/invoices/statement/rollback`)
    return response?.data
}

export const getAllServicesByCustomerID = async (
    payload: {
        customer_id: number
    },
    t: TFunction
) => {
    const response = await datacentral.get<ResponseArray<Service>>(
        `/customers/${payload.customer_id}/all_services`
    )
    if (!response?.data?.results) return []
    const options = response?.data?.results?.map((item) => {
        const status = SERVICE_STATUS(t)?.find(
            (status) => status.value === item?.service_status
        )
        const label = [
            item?.service_id,
            item?.service_name,
            status?.label,
            item.name,
            item.is_poc ? '_POC' : '',
        ]
            .filter(Boolean)
            .join(' - ')
        return {
            label,
            value: item?.service_id,
        }
    })
    return options
}

export const getServiceAddonConfigoptions = async ({
    serviceID,
}: {
    serviceID: number
}) => {
    const response = await datacentral.get<
        ResponseArray<
            Service & {
                configoptions: ServiceConfigoption[]
            }
        >
    >(`/services/${serviceID}/addons/configoptions`)
    // API returns `results: null` when the service has no addons — coalesce to []
    // so callers can safely use .length / .map without a null crash.
    return response?.data?.results ?? []
}

export const updateServiceMetadata = async ({
    serviceID,
    metadata,
}: {
    serviceID: number
    metadata: any
}) => {
    const response = await datacentral.put(`/services/${serviceID}/metadata`, {
        metadata,
    })
    return response?.data
}

// updateServiceBillingOrganization sets/clears the margarita organization_id the CDN
// reconcile must query for this service (service_metadata.billing_organization_id).
// Empty string clears the override (falls back to the datacentral customer_id).
// Super-admin only; only valid for SwiftFederation.CDN services.
export const updateServiceBillingOrganization = async ({
    serviceID,
    billing_organization_id,
}: {
    serviceID: number
    billing_organization_id: string
}) => {
    const response = await datacentral.put(
        `/services/${serviceID}/metadata/billing-organization`,
        { billing_organization_id }
    )
    return response?.data
}

// ServiceUUIDAssignmentOption is one selectable margarita CDN service family for the config
// picker: id = margarita services.id (stored as service_uuid_assignment), name = label.
export type ServiceUUIDAssignmentOption = { id: string; name: string }

// getServiceUUIDAssignmentOptions lists the margarita CDN service families under this CDN
// service's org, so the config UI can map service_creatable_uuid -> margarita services.id
// (service_uuid_assignment) for a service under a multi-service org (e.g. CMC VTVCDS vs VTV).
export const getServiceUUIDAssignmentOptions = async (serviceID: number) => {
    const response = await datacentral.get<
        ResponseArray<ServiceUUIDAssignmentOption>
    >(`/services/${serviceID}/metadata/service-uuid-assignment/options`)
    return response?.data?.results ?? []
}

// updateServiceUUIDAssignment sets/clears service_metadata.service_uuid_assignment — the
// margarita service id whose FAMILY this CDN service reconciles against (empty clears →
// falls back to org scope). Super-admin only; SwiftFederation.CDN services only.
export const updateServiceUUIDAssignment = async ({
    serviceID,
    service_uuid_assignment,
}: {
    serviceID: number
    service_uuid_assignment: string
}) => {
    const response = await datacentral.put(
        `/services/${serviceID}/metadata/service-uuid-assignment`,
        { service_uuid_assignment }
    )
    return response?.data
}

export type UpdateOtherStandardSchedulePayload = {
    serviceID: number
    payment_method: string
    payment_mode: string
    description?: string
    ranges: Array<{
        start_date: string
        end_date: string
        price: number
    }>
}

export const updateOtherStandardSchedule = async ({
    serviceID,
    ...payload
}: UpdateOtherStandardSchedulePayload) => {
    const response = await datacentral.put(
        `/services/${serviceID}/metadata/other-standard-schedule`,
        payload
    )
    return response?.data
}

export const useServiceWithAddonConfig = (serviceId?: number) => {
    return useQueryWithHandler<{
        configoptions: ServiceConfigoption[]
        addons: (Service & {
            configoptions: ServiceConfigoption[]
        })[]
    }>({
        queryKey: [QUERY_KEYS.SERVICES_ADDON_PRICES, serviceId],
        queryFn: async () => {
            if (!serviceId) return { configoptions: [], addons: [] }

            const [configoptions, addons] = await Promise.all([
                getServiceConfigoptions({ serviceID: serviceId }),
                getServiceAddonConfigoptions({ serviceID: serviceId }),
            ])

            return { configoptions, addons }
        },
        enabled: Boolean(serviceId),
    })
}

export const getServiceNotificationPreview = async ({
    serviceID,
    notificationID,
    mailerID,
}: {
    serviceID: number
    notificationID: number
    mailerID: number
}) => {
    const response = await datacentral.get(
        `/services/${serviceID}/technical/${notificationID}/preview.extend.html?mailer_id=${mailerID}`
    )
    return response?.data
}

export const getServiceAlerts = async (serviceID: number) => {
    const response = await datacentral.get<ResponseArray<ServiceAlert>>(
        `/services/${serviceID}/alerts`
    )
    return response?.data
}

export const createAlerts = async (
    serviceID: number,
    data: AlertThreshold[]
) => {
    const response = await datacentral.post(
        `/services/${serviceID}/alerts/upsert`,
        {
            thresholds: data,
        }
    )
    return response?.data
}

export const getServiceAlertHistory = async ({
    serviceID,
    params,
}: {
    serviceID: number
    params: Pick<DataGridPayload, 'page' | 'page_size'>
}) => {
    const response = await datacentral.get<ResponseArray<ActivityType>>(
        `/services/${serviceID}/alerts/history?${queryString.stringify(params)}`
    )
    return response?.data
}

export const useServiceWithAddonConfigPrices = (serviceId?: number) => {
    return useQueryWithHandler<{
        configoptions: ServiceConfigoption[]
        addons: (Service & {
            configoptions: ServiceConfigoption[]
        })[]
    }>({
        queryKey: [QUERY_KEYS.SERVICES_AVAILABLE_ADDONS, serviceId],
        queryFn: async () => {
            if (!serviceId) return { configoptions: [], addons: [] }

            const [configoptions, addons] = await Promise.all([
                getServiceConfigoptionPrices({ serviceID: serviceId }),
                getServiceAddonConfigoptions({ serviceID: serviceId }),
            ])

            return { configoptions, addons }
        },
        enabled: Boolean(serviceId),
    })
}

export const updateServiceTaxPercentage = async ({
    serviceID,
    service_tax_percentage,
    note,
}: {
    serviceID: number
    service_tax_percentage: number
    note?: string
}) => {
    const response = await datacentral.put(
        `/services/${serviceID}/tax-percentage`,
        {
            service_tax_percentage,
            note,
        }
    )
    return response?.data
}

export const updateServiceType = async ({
    serviceID,
    service_type,
}: {
    serviceID: number
    service_type: string
}) => {
    const response = await datacentral.put(
        `/services/${serviceID}/service-type`,
        {
            service_type,
        }
    )
    return response?.data
}

export const updateServiceEmployeeSaleTracking = async ({
    serviceID,
    employee_sale_tracking_ids = [],
}: {
    serviceID: number
    employee_sale_tracking_ids: number[]
}) => {
    const response = await datacentral.put(
        `/services/${serviceID}/commissions`,
        {
            employee_sale_tracking_ids,
        }
    )
    return response?.data
}

export const getServiceProviderByServiceTemplate = async (
    serviceTemplate: string,
    search: string
) => {
    const response = await datacentral.get<ResponseArray<ServiceVNISProvider>>(
        `/services/providers/${serviceTemplate}?search=${search}`
    )
    return uniqBy(response?.data?.results, 'customerId') || []
}

export const mergeServiceConfigoptions = async ({
    serviceID,
    service_configoption_ids,
    keep_configoption_id,
}: {
    serviceID: number
    service_configoption_ids: number[]
    keep_configoption_id: number
}) => {
    const response = await datacentral.post<
        ResponseObject<ServiceConfigoption>
    >(`/services/${serviceID}/configoptions/merge`, {
        service_configoption_ids,
        keep_configoption_id,
    })
    return response?.data
}

export const updateServiceCurrencyExchangeRate = async ({
    serviceID,
    service_currency,
    service_exchange_rate,
}: {
    serviceID: number
    service_currency: string
    service_exchange_rate: number
}) => {
    const response = await datacentral.put(
        `/services/${serviceID}/currency-exchange-rate`,
        {
            service_currency,
            service_exchange_rate,
        }
    )
    return response?.data
}

// Service Prices CRUD
export const getServicePrices = async (serviceID: number) => {
    const response = await datacentral.get<ResponseArray<ServicePrice>>(
        `/services/${serviceID}/prices`
    )
    return response?.data?.results || []
}

export const createServicePrice = async ({
    serviceID,
    data,
}: {
    serviceID: number
    data: CreateServicePricePayload
}) => {
    const response = await datacentral.post(
        `/services/${serviceID}/prices`,
        data
    )
    return response?.data
}

export const updateServicePrice = async ({
    serviceID,
    priceID,
    data,
}: {
    serviceID: number
    priceID: number
    data: UpdateServicePricePayload
}) => {
    const response = await datacentral.put(
        `/services/${serviceID}/prices/${priceID}`,
        data
    )
    return response?.data
}

export const deleteServicePrice = async ({
    serviceID,
    priceID,
}: {
    serviceID: number
    priceID: number
}) => {
    const response = await datacentral.delete(
        `/services/${serviceID}/prices/${priceID}`
    )
    return response?.data
}

// Config Option Price History CRUD
export const getConfigOptionPriceHistory = async ({
    serviceID,
    configID,
}: {
    serviceID: number
    configID: number
}) => {
    const response = await datacentral.get<
        ResponseArray<
            import('@/modules/Service/types').ConfigOptionPriceHistory
        >
    >(`/services/${serviceID}/configoptions/${configID}/price-history`)
    return response?.data?.results || []
}

export const createConfigOptionPrice = async ({
    serviceID,
    configID,
    data,
}: {
    serviceID: number
    configID: number
    data: import('@/modules/Service/types').CreateConfigOptionPricePayload
}) => {
    const response = await datacentral.post(
        `/services/${serviceID}/configoptions/${configID}/price-history`,
        data
    )
    return response?.data
}

export const updateConfigOptionPrice = async ({
    serviceID,
    configID,
    priceID,
    data,
}: {
    serviceID: number
    configID: number
    priceID: number
    data: import('@/modules/Service/types').UpdateConfigOptionPricePayload
}) => {
    const response = await datacentral.put(
        `/services/${serviceID}/configoptions/${configID}/price-history/${priceID}`,
        data
    )
    return response?.data
}

export const deleteConfigOptionPrice = async ({
    serviceID,
    configID,
    priceID,
}: {
    serviceID: number
    configID: number
    priceID: number
}) => {
    const response = await datacentral.delete(
        `/services/${serviceID}/configoptions/${configID}/price-history/${priceID}`
    )
    return response?.data
}

// Service VAT Rates
export const getServiceVatRates = async (serviceID: number) => {
    const response = await datacentral.get<ResponseArray<ServiceVatRate>>(
        `/services/${serviceID}/vat-rates`
    )
    return response?.data?.results || []
}

export const createServiceVatRate = async ({
    serviceID,
    data,
}: {
    serviceID: number
    data: {
        tax_rate: number
        effective_from: string
        effective_to?: string
        description?: string
    }
}) => {
    const response = await datacentral.post(
        `/services/${serviceID}/vat-rates`,
        data
    )
    return response?.data
}

export const updateServiceVatRate = async ({
    serviceID,
    vatRateID,
    data,
}: {
    serviceID: number
    vatRateID: number
    data: {
        tax_rate?: number
        effective_from?: string
        effective_to?: string | null
        description?: string
    }
}) => {
    const response = await datacentral.put(
        `/services/${serviceID}/vat-rates/${vatRateID}`,
        data
    )
    return response?.data
}

export const deleteServiceVatRate = async ({
    serviceID,
    vatRateID,
}: {
    serviceID: number
    vatRateID: number
}) => {
    const response = await datacentral.delete(
        `/services/${serviceID}/vat-rates/${vatRateID}`
    )
    return response?.data
}

// Price Change Requests
export const getPriceChangeRequests = async (params?: { status?: string }) => {
    const query = params?.status ? `?status=${params.status}` : ''
    const response = await datacentral.get<ResponseArray<PriceChangeRequest>>(
        `/services/price-change-requests${query}`
    )
    return response?.data?.results || []
}

export const getServicePriceChangeRequests = async (serviceID: number) => {
    const response = await datacentral.get<ResponseArray<PriceChangeRequest>>(
        `/services/${serviceID}/price-change-requests`
    )
    return response?.data?.results || []
}

export const applyPriceChangeRequest = async ({
    id,
    effective_from,
}: {
    id: number
    effective_from: string
}) => {
    const response = await datacentral.post(
        `/services/price-change-requests/${id}/apply`,
        { effective_from }
    )
    return response?.data
}

export const rejectPriceChangeRequest = async ({
    id,
    note,
}: {
    id: number
    note?: string
}) => {
    const response = await datacentral.post(
        `/services/price-change-requests/${id}/reject`,
        { note }
    )
    return response?.data
}
